import {z} from 'zod';
import {congestionAreaClusterSchema, MAX_GUARD_MW} from './congestion-area-cluster';
import {congestionAreaClusterIdSchema, congestionAreaIdSchema, organizationIdSchema} from './id';

/** EAN-13 as the DSO is identified by on the wire: thirteen digits, no separators. */
const ean13Schema = z.string().regex(/^\d{13}$/);

/**
 * A congestion area belongs to one organization and is what an afroep request is made
 * against. `congestionPointId` is Groupie's identifier for the point behind it, and
 * `programId` is the OpenADR program the events are published into.
 */
export const congestionAreaSchema = z.object({
  id: congestionAreaIdSchema,
  organizationId: organizationIdSchema,
  congestionPointId: z.string().trim().min(1).max(64),
  name: z.string().trim().min(1).max(200),
  dsoEan13: ean13Schema.max(13),
  programId: z.string().trim().min(1).max(64),
  /**
   * Whether a request may be made against it. An organisation administrator turns this
   * off — the area keeps its requests and its history, it simply stops being offered.
   * Deliberately not deletion, which the platform admin owns and which existing requests
   * refuse anyway.
   */
  active: z.boolean(),
  /**
   * The area's own MW ceiling, or null for none. It applies before the cluster's, to this
   * area alone (ADR 0038).
   */
  guardMw: z.number().nonnegative().max(MAX_GUARD_MW).nullable(),
  /**
   * The cluster whose MW ceiling covers this area, or null for an area under none.
   * Membership is set with SQL rather than through this tool — see
   * `congestion-area-cluster.ts`.
   */
  clusterId: congestionAreaClusterIdSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CongestionArea = z.infer<typeof congestionAreaSchema>;

/**
 * What a platform admin fills in. The organization is part of the body rather than the
 * path because this is the one decision that *assigns* an area to a tenant — a caller who
 * can create one can create it anywhere, and saying which is the whole point.
 *
 * `congestionPointId` is Groupie's identifier and decides which resource groups the
 * organization can read at all; `programId` is the OpenADR program its events go into.
 */
export const createCongestionAreaSchema = z
  .object({
    organizationId: organizationIdSchema,
    congestionPointId: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(200),
    dsoEan13: ean13Schema,
    programId: z.string().trim().min(1).max(64),
  })
  .strict();

export type CreateCongestionArea = z.infer<typeof createCongestionAreaSchema>;

/**
 * Everything except the organization: moving an area between tenants would carry its
 * requests with it, and the history of who dispatched what would quietly change hands.
 * Remove it and configure it again instead.
 */
export const updateCongestionAreaSchema = z
  .object({
    congestionPointId: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(200),
    dsoEan13: ean13Schema,
    programId: z.string().trim().min(1).max(64),
  })
  .strict()
  .partial()
  .refine(value => Object.keys(value).length > 0, {message: 'NO_FIELDS_TO_UPDATE'});

export type UpdateCongestionArea = z.infer<typeof updateCongestionAreaSchema>;

/**
 * What an organisation administrator may change about an area of their own organisation,
 * and the whole of it: whether it is offered when a request is made, and its own MW
 * ceiling. The congestion point, program and DSO decide what the area *is* and stay with
 * the platform admin — an organisation cannot repoint its own area at somebody else's grid.
 *
 * Its own schema rather than a widening of `updateCongestionAreaSchema`, because one route
 * that means two things depending on who calls it is how a policy check gets missed.
 *
 * **Zero and `null` both mean "no ceiling", and are stored as `null`**, as on the cluster
 * (`configureCongestionAreaClusterSchema`).
 */
export const configureCongestionAreaSchema = z
  .object({
    active: z.boolean(),
    guardMw: z
      .number()
      .nonnegative()
      .max(MAX_GUARD_MW)
      .nullable()
      .transform(value => (value ? value : null)),
  })
  .strict()
  .partial()
  .refine(value => Object.keys(value).length > 0, {message: 'NO_FIELDS_TO_UPDATE'});

export type ConfigureCongestionArea = z.infer<typeof configureCongestionAreaSchema>;

/**
 * An area as a list or a detail read hands it over: the row plus the cluster it sits in,
 * resolved server-side. Joined rather than looked up in the browser for the reason every
 * such join is — a list of ids the frontend resolves itself is a second request and a
 * second chance to show the wrong name.
 *
 * `cluster` is null for an area in none, which is the common case.
 */
export const congestionAreaWithClusterSchema = congestionAreaSchema.extend({
  cluster: congestionAreaClusterSchema.pick({id: true, code: true, name: true, guardMw: true}).nullable(),
});

export type CongestionAreaWithCluster = z.infer<typeof congestionAreaWithClusterSchema>;
